import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Grid, 
  Typography, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select,
  Box,
  Paper,
  FormHelperText,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Divider
} from '@mui/material';
import { apiService } from '../../utils/api';
import { sendVerificationEmail } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { AreaSelector } from '../AreaSelector';
import { useLocation, useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const CUP_SIZES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];

const PosterRegistrationForm = ({ userId }) => {
  const { currentUser, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneShops, setPhoneShops] = useState([]);
  const [isCheckingPhoneShops, setIsCheckingPhoneShops] = useState(false);
  
  // 企業ユーザーからのデータを取得
  const linkState = location.state || {};
  
  // 都道府県データが初期値として設定された場合、市区町村データを読み込む
  useEffect(() => {
    if (formData.prefecture_code) {
      fetchCities(formData.prefecture_code);
    }
  }, []);
  
  // 入力フォーム状態
  const [formData, setFormData] = useState({
    username: linkState.username || '',
    email: '',
    password: '',
    phone_number: '',
    shop_name: linkState.shop_name || '',
    prefecture_code: linkState.prefecture_code || '',
    city_code: '',
    stage_name: '',
    age: '',
    height: '',
    bust: '',
    cup_size: '',
    waist: '',
    hip: ''
  });
  
  // 都道府県と市区町村のデータ
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  
  // 初期データ読み込み
  useEffect(() => {
    const fetchPrefectures = async () => {
      try {
        const response = await apiService.get('/api/areas/prefectures');
        setPrefectures(response.data);
      } catch (error) {
        setError('都道府県データの取得に失敗しました');
      }
    };
    
    // ユーザーの既存データがあれば取得
    const fetchUserData = async () => {
      if (userId) {
        try {
          const response = await apiService.get(`/api/users/profile/${userId}`);
          const userData = response.data;
          
          if (userData.poster_profile) {
            const profile = userData.poster_profile;
            
            setFormData({
              ...formData,
              shop_name: profile.shop_name || '',
              prefecture_code: profile.prefecture?.code || '',
              city_code: profile.city?.code || '',
              stage_name: profile.stage_name || '',
              age: profile.age || '',
              height: profile.height || '',
              bust: profile.bust || '',
              cup_size: profile.cup_size || '',
              waist: profile.waist || '',
              hip: profile.hip || ''
            });
            
            // 都道府県が選択されている場合は市区町村を読み込む
            if (profile.prefecture?.code) {
              fetchCities(profile.prefecture.code);
            }
          }
        } catch (error) {
          setError('ユーザーデータの取得に失敗しました');
        }
      }
    };
    
    fetchPrefectures();
    fetchUserData();
  }, [userId]);
  
  // 都道府県が変更されたら市区町村を取得
  const fetchCities = async (prefectureCode) => {
    if (!prefectureCode) {
      setCities([]);
      return;
    }
    
    try {
      const response = await apiService.get(`/api/areas/cities/${prefectureCode}`);
      setCities(response.data);
    } catch (error) {
      setError('市区町村データの取得に失敗しました');
    }
  };

  // ユーザー名の可用性をチェック
  const checkUsernameAvailability = async (username) => {
    if (!username || username.length < 3) return;
    
    setIsCheckingUsername(true);
    try {
      const response = await apiService.get(`/api/auth/check-username?username=${encodeURIComponent(username)}`);
      setUsernameAvailable(response.data.available);
    } catch (error) {
      setUsernameAvailable(false);
    } finally {
      setIsCheckingUsername(false);
    }
  };
  
  // パスワード表示・非表示の切り替え
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };
  
  // 入力値の変更を処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // 都道府県が変更された場合の特別処理
    if (name === 'prefecture_code') {
      // 都道府県変更時に市区町村をクリアして、新しい市区町村リストを取得
      setFormData(prev => ({
        ...prev,
        [name]: value,
        city_code: '' // 市区町村をリセット
      }));
      
      // 新しい市区町村リストを取得
      fetchCities(value);
    } else {
      // 通常の入力値変更処理
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // ユーザー名変更時に可用性チェック
    if (name === 'username' && value.length >= 3) {
      checkUsernameAvailability(value);
    }

    // 電話番号変更時に在籍店舗をチェック（複数店舗在籍の案内用）
    if (name === 'phone_number') {
      const digits = (value || '').replace(/[^0-9]/g, '');
      if (digits.length >= 10 && digits.length <= 11) {
        checkPhoneShops(value);
      } else {
        setPhoneShops([]);
      }
    }
  };

  // 電話番号に紐づく在籍店舗を取得（登録はブロックせず案内のみ）
  const checkPhoneShops = async (phoneNumber) => {
    try {
      setIsCheckingPhoneShops(true);
      const response = await apiService.get(`/api/auth/phone-shops?phone_number=${encodeURIComponent(phoneNumber)}`);
      const shops = (response.data && response.data.shops) || [];
      setPhoneShops(shops);
    } catch (err) {
      console.warn('在籍店舗チェックに失敗:', err?.message);
      setPhoneShops([]);
    } finally {
      setIsCheckingPhoneShops(false);
    }
  };

  // バリデーション関数
  const validateEmail = () => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !formData.email || re.test(formData.email);
  };
  
  const validatePassword = () => {
    return !formData.password || formData.password.length >= 8;
  };
  
  const validateUsername = () => {
    return !formData.username || (formData.username.length >= 3 && usernameAvailable);
  };
  
  // 数値フィールドの検証
  const validateNumberField = (value, min, max) => {
    if (!value) return true;
    const num = parseInt(value);
    return !isNaN(num) && num >= min && num <= max;
  };
  
  // フォームの送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    
    // ユーザー情報のバリデーション
    if (!formData.username || formData.username.length < 3) {
      setError('ユーザー名は3文字以上で入力してください');
      return;
    }
    
    if (!usernameAvailable) {
      setError('このユーザー名は既に使用されています');
      return;
    }
    
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('有効なメールアドレスを入力してください');
      return;
    }
    
    if (!formData.password || formData.password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }
    
    // 電話番号のバリデーション
    if (!formData.phone_number || formData.phone_number.length < 10 || formData.phone_number.length > 11) {
      setError('電話番号は10桁または11桁で入力してください');
      return;
    }
    
    // 投稿ユーザー情報のバリデーション
    if (!formData.shop_name) {
      setError('店名は必須です');
      return;
    }
    
    if (!validateNumberField(formData.age, 18, 100)) {
      setError('年齢は18歳以上で入力してください');
      return;
    }
    
    if (!validateNumberField(formData.height, 140, 200)) {
      setError('身長は140cm〜200cmの範囲で入力してください');
      return;
    }
    
    if (!validateNumberField(formData.bust, 70, 150)) {
      setError('バストは70cm〜150cmの範囲で入力してください');
      return;
    }
    
    if (!validateNumberField(formData.waist, 50, 120)) {
      setError('ウエストは50cm〜120cmの範囲で入力してください');
      return;
    }
    
    if (!validateNumberField(formData.hip, 70, 150)) {
      setError('ヒップは70cm〜150cmの範囲で入力してください');
      return;
    }
    
    setLoading(true);
    
    try {
      // 新規ユーザー登録APIを呼び出す
      setLoading(true);
      
      // 標準的な登録APIエンドポイントを使用
      console.log('ユーザー登録API呼び出し開始:', {
        username: formData.username,
        email: formData.email,
        type: 'poster',
        shop_name: formData.shop_name,
        phone_number: formData.phone_number
      });
      
      const registerResponse = await apiService.post('/api/auth/register', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        type: 'poster', // ユーザータイプを指定
        shop_name: formData.shop_name,
        phone_number: formData.phone_number, // フォームから入力された電話番号を使用
      });
      
      console.log('ユーザー登録API完全レスポンス:', registerResponse);
      console.log('レスポンスデータ:', registerResponse.data);
      console.log('user_idフィールド:', registerResponse.data?.user_id);
      console.log('userオブジェクト:', registerResponse.data?.user);
      
      if (!registerResponse.data || !registerResponse.data.user_id) {
        console.error('user_idが見つかりません。レスポンス構造:', JSON.stringify(registerResponse.data, null, 2));
        throw new Error('ユーザー登録に失敗しました。レスポンスにユーザーIDが含まれていません。');
      }
      
      const userId = registerResponse.data.user_id;

      // 確認メールを送信（Firebase が独自ドメイン設定に従って送信）。失敗しても登録は継続。
      try {
        await sendVerificationEmail(formData.email, formData.password);
      } catch (mailErr) {
        console.warn('確認メール送信に失敗しました（登録は継続）:', mailErr?.code || mailErr?.message);
      }
      
      // 投稿ユーザー情報の登録
      const posterResponse = await apiService.post(`/api/users/poster_profile/${userId}`, {
        shop_name: formData.shop_name,
        prefecture_code: formData.prefecture_code,
        city_code: formData.city_code,
        stage_name: formData.stage_name,
        age: formData.age,
        height: formData.height,
        bust: formData.bust,
        cup_size: formData.cup_size,
        waist: formData.waist,
        hip: formData.hip
      });
      
      // 企業ユーザーのタグ付けユーザーリストに追加
      try {
        console.log('タグ付けユーザー登録を開始:', {
          username: formData.username,
          email: formData.email,
          tags: [formData.shop_name]
        });
        
        const tagResponse = await apiService.post('/api/corporate/tagged-users', {
          username: formData.username,
          email: formData.email,
          tags: [formData.shop_name] // 店名をタグとして使用
        });
        
        console.log('タグ付けユーザー登録成功:', tagResponse.data);
      } catch (tagError) {
        console.error('タグ付けユーザー登録失敗:', {
          error: tagError.message,
          response: tagError.response?.data,
          status: tagError.response?.status
        });
        
        // タグ付け失敗の場合もエラーとして表示
        setError(`ユーザー登録は完了しましたが、タグ付けユーザーリストへの追加に失敗しました。詳細: ${tagError.response?.data?.message || tagError.message}`);
        setLoading(false);
        return;
      }
      
      setSuccess(true);
      
      // 3秒後に投稿ユーザー一覧にリダイレクト
      setTimeout(() => {
        navigate('/corporate/tagged-users');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.message || error.message || '登録に失敗しました。システム管理者にお問い合わせください。');
      setLoading(false);
    }
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        投稿ユーザー登録
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          投稿ユーザー情報を登録しました。3秒後にタグ付けユーザー一覧に移動します。
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              ユーザー登録情報
            </Typography>
          </Grid>
          
          {/* ユーザー名 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ユーザー名 (必須)"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              helperText={
                formData.username 
                  ? isCheckingUsername 
                    ? 'ユーザー名をチェック中...' 
                    : usernameAvailable 
                      ? '使用可能なユーザー名です' 
                      : 'このユーザー名は使用できません'
                  : '@から始まるユーザー名を3文字以上で入力してください'
              }
              error={formData.username !== '' && (!usernameAvailable || formData.username.length < 3)}
              InputProps={{
                startAdornment: <span>@</span>,
              }}
            />
          </Grid>
          
          {/* メールアドレス */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="メールアドレス (必須)"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              helperText="有効なメールアドレスを入力してください"
              error={formData.email !== '' && !validateEmail()}
            />
          </Grid>
          
          {/* パスワード */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="パスワード (必須)"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              helperText="8文字以上のパスワードを設定してください"
              error={formData.password !== '' && !validatePassword()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="パスワードの表示切替"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          {/* 電話番号 */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="電話番号 (必須)"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              required
              helperText={
                isCheckingPhoneShops
                  ? '在籍状況を確認しています...'
                  : 'ハイフンなしで入力してください（例：09012345678）'
              }
              inputProps={{ maxLength: 11, pattern: "[0-9]*" }}
            />
            {phoneShops.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                この電話番号は現在、次の店舗に在籍登録があります：
                <strong>{phoneShops.join('、')}</strong>
                <br />
                別店舗として続けて登録できます（複数店舗への在籍が可能です）。
              </Alert>
            )}
          </Grid>
          
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              店舗・身体情報
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="店名 (必須)"
              name="shop_name"
              value={formData.shop_name}
              onChange={handleChange}
              required
              helperText="投稿時に固定タグとして設定されます"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>都道府県</InputLabel>
              <Select
                name="prefecture_code"
                value={formData.prefecture_code}
                onChange={handleChange}
                label="都道府県"
              >
                <MenuItem value="">選択なし</MenuItem>
                {prefectures.map(prefecture => (
                  <MenuItem key={prefecture.code} value={prefecture.code}>
                    {prefecture.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>都道府県を選択してください</FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!formData.prefecture_code}>
              <InputLabel>市区町村</InputLabel>
              <Select
                name="city_code"
                value={formData.city_code}
                onChange={handleChange}
                label="市区町村"
              >
                <MenuItem value="">選択なし</MenuItem>
                {cities.map(city => (
                  <MenuItem key={city.code} value={city.code}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>市区町村を選択してください</FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="源氏名"
              name="stage_name"
              value={formData.stage_name}
              onChange={handleChange}
              helperText="源氏名を入力してください"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="年齢"
              name="age"
              type="number"
              value={formData.age}
              onChange={handleChange}
              InputProps={{ inputProps: { min: 18, max: 100 } }}
              helperText="18歳以上で入力してください"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="身長 (cm)"
              name="height"
              type="number"
              value={formData.height}
              onChange={handleChange}
              InputProps={{ inputProps: { min: 140, max: 200 } }}
              helperText="140cm〜200cmで入力してください"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="バスト (cm)"
              name="bust"
              type="number"
              value={formData.bust}
              onChange={handleChange}
              InputProps={{ inputProps: { min: 70, max: 150 } }}
              helperText="70cm〜150cmで入力してください"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>カップ数</InputLabel>
              <Select
                name="cup_size"
                value={formData.cup_size}
                onChange={handleChange}
                label="カップ数"
              >
                <MenuItem value="">選択なし</MenuItem>
                {CUP_SIZES.map(size => (
                  <MenuItem key={size} value={size}>
                    {size}カップ
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>カップ数を選択してください</FormHelperText>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="ウエスト (cm)"
              name="waist"
              type="number"
              value={formData.waist}
              onChange={handleChange}
              InputProps={{ inputProps: { min: 50, max: 120 } }}
              helperText="50cm〜120cmで入力してください"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="ヒップ (cm)"
              name="hip"
              type="number"
              value={formData.hip}
              onChange={handleChange}
              InputProps={{ inputProps: { min: 70, max: 150 } }}
              helperText="70cm〜150cmで入力してください"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mr: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : '登録'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.history.back()}
              >
                戻る
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default PosterRegistrationForm; 